/**
 * x-duftfinder.js — regelbasiertes Quiz (Abschnitt 5.5), komplett
 * client-seitig aus assets/x-quiz-data.json. Vanilla Web Component.
 *
 * 5 Fragen, je 1 Screen, Fortschrittsbalken:
 *   1. Für wen? (E13: Für Sie / Für Ihn / Nische / Egal)
 *   2. Frisch ↔ Warm
 *   3. Bevorzugte Welt (8 Familien, max. 2)
 *   4. Anlass
 *   5. Gewünschte Präsenz (dezent ↔ raumfüllend)
 *
 * Ergebnis: 3–5 Produkte mit Begründung, teilbar per URL-Query (?q=...).
 */

if (!customElements.get('x-duftfinder')) {
  customElements.define(
    'x-duftfinder',
    class XDuftfinder extends HTMLElement {
      async connectedCallback() {
        const res = await fetch(this.dataset.quelle);
        this.produkte = (await res.json()).produkte;

        this.fragen = [
          {
            id: 'wer',
            titel: 'Für wen suchst du?',
            optionen: [
              { key: 'damen', label: 'Für Sie' },
              { key: 'herren', label: 'Für Ihn' },
              { key: 'nische', label: 'Nische – das Besondere' },
              { key: 'egal', label: 'Zeig mir alles' },
            ],
            max: 1,
          },
          {
            id: 'temperatur',
            titel: 'Eher frisch oder eher warm?',
            optionen: [
              { key: 'frisch', label: 'Frisch & klar' },
              { key: 'mitte', label: 'Dazwischen' },
              { key: 'warm', label: 'Warm & umhüllend' },
            ],
            max: 1,
          },
          {
            id: 'welt',
            titel: 'Welche Duftwelt zieht dich an? (max. 2)',
            optionen: [
              { key: 'frisch-zitrisch', label: 'Frisch & Zitrisch' },
              { key: 'aquatisch-klar', label: 'Aquatisch & Klar' },
              { key: 'aromatisch-gruen', label: 'Aromatisch & Grün' },
              { key: 'blumig', label: 'Blumig' },
              { key: 'fruchtig-suess', label: 'Fruchtig & Süß' },
              { key: 'holzig-wuerzig', label: 'Holzig & Würzig' },
              { key: 'amber-orientalisch', label: 'Amber & Orientalisch' },
              { key: 'gourmand', label: 'Gourmand' },
            ],
            max: 2,
          },
          {
            id: 'anlass',
            titel: 'Wofür soll der Duft sein?',
            optionen: [
              { key: 'alltag', label: 'Alltag' },
              { key: 'büro', label: 'Büro' },
              { key: 'abend', label: 'Abend' },
              { key: 'besonderer_anlass', label: 'Besonderer Anlass' },
              { key: 'sport_freizeit', label: 'Sport & Freizeit' },
            ],
            max: 1,
          },
          {
            id: 'praesenz',
            titel: 'Wie präsent darf er sein?',
            optionen: [
              { key: 'dezent', label: 'Dezent, nah an der Haut' },
              { key: 'mittel', label: 'Spürbar, aber höflich' },
              { key: 'stark', label: 'Raumfüllend' },
            ],
            max: 1,
          },
        ];

        this.antworten = this.leseUrl() ?? {};
        this.schritt = Object.keys(this.antworten).length >= 5 ? 5 : 0;
        this.render();
      }

      leseUrl() {
        const q = new URLSearchParams(location.search).get('q');
        if (!q) return null;
        try {
          const parsed = JSON.parse(atob(q));
          return typeof parsed === 'object' ? parsed : null;
        } catch {
          return null;
        }
      }

      render() {
        if (this.schritt >= this.fragen.length) return this.renderErgebnis();
        const frage = this.fragen[this.schritt];
        const gewaehlt = this.antworten[frage.id] ?? [];

        this.innerHTML = `
          <div class="x-quiz">
            <div class="x-quiz__fortschritt" role="progressbar" aria-valuemin="0" aria-valuemax="5" aria-valuenow="${this.schritt + 1}">
              <div class="x-quiz__fortschritt-balken" style="--quiz-fortschritt:${(this.schritt + 1) / 5}"></div>
            </div>
            <h2 class="x-quiz__frage">${frage.titel}</h2>
            <div class="x-quiz__optionen" role="group" aria-label="${frage.titel}">
              ${frage.optionen
                .map(
                  (o) => `<button type="button" class="x-quiz__option" data-key="${o.key}"
                    aria-pressed="${gewaehlt.includes(o.key)}">${o.label}</button>`,
                )
                .join('')}
            </div>
            <div class="x-quiz__weiter">
              ${this.schritt === 0 ? '' : '<button type="button" class="x-button x-button--sekundaer" data-zurueck>Zurück</button>'}
              <button type="button" class="x-button x-button--primaer" data-weiter ${gewaehlt.length === 0 ? 'disabled' : ''}>Weiter</button>
            </div>
          </div>`;

        this.querySelectorAll('.x-quiz__option').forEach((btn) => {
          btn.addEventListener('click', () => {
            const key = btn.dataset.key;
            let list = this.antworten[frage.id] ?? [];
            if (list.includes(key)) list = list.filter((k) => k !== key);
            else if (frage.max === 1) list = [key];
            else if (list.length < frage.max) list = [...list, key];
            this.antworten[frage.id] = list;
            this.render();
          });
        });
        this.querySelector('[data-weiter]')?.addEventListener('click', () => {
          this.schritt++;
          this.render();
        });
        this.querySelector('[data-zurueck]')?.addEventListener('click', () => {
          this.schritt = Math.max(0, this.schritt - 1);
          this.render();
        });
      }

      bewerte(p) {
        const a = this.antworten;
        let score = 0;
        const gruende = [];

        const wer = (a.wer ?? [])[0];
        if (wer === 'damen' || wer === 'herren') {
          if (p.zielgruppe !== wer && p.zielgruppe !== 'unisex') return null;
          if (p.zielgruppe === wer) score += 2;
        } else if (wer === 'nische') {
          if (p.segment !== 'nische') return null;
          score += 2;
          gruende.push('Nische');
        }

        const temp = (a.temperatur ?? [])[0];
        const warmeFamilien = ['amber-orientalisch', 'gourmand', 'holzig-wuerzig'];
        const frischeFamilien = ['frisch-zitrisch', 'aquatisch-klar', 'aromatisch-gruen'];
        if (temp === 'warm' && (warmeFamilien.includes(p.familie) || p.charakter.includes('warm'))) {
          score += 2;
          gruende.push('warm');
        }
        if (temp === 'frisch' && (frischeFamilien.includes(p.familie) || p.charakter.includes('frisch'))) {
          score += 2;
          gruende.push('frisch');
        }
        if (temp === 'mitte') score += 1;

        for (const welt of a.welt ?? []) {
          if (p.familie === welt) {
            score += 3;
            gruende.push(this.familienName(welt));
          } else if (p.fam2 === welt) {
            score += 1.5;
          }
        }

        const anlass = (a.anlass ?? [])[0];
        if (anlass && p.anlass.includes(anlass)) {
          score += 2;
          gruende.push(this.anlassName(anlass));
        }

        const praesenz = (a.praesenz ?? [])[0];
        if (praesenz === 'dezent' && p.sillage <= 2) score += 2;
        if (praesenz === 'mittel' && p.sillage === 3) score += 2;
        if (praesenz === 'stark' && p.sillage >= 4) {
          score += 2;
          gruende.push('präsent');
        }

        return { p, score, gruende };
      }

      /* Quick-Add mit echten Varianten (IDs/Preise aus x-quiz-data.json, Shopify-Daten) —
     mehrere Varianten: erst Groesse waehlen, dann echte Variant-ID ueber /cart/add (Dawn product-form). */
  quickAdd(p) {
    const vs = (p.varianten || []).filter((v) => v.verfuegbar);
    if (!vs.length) return `<button type="button" class="x-button x-button--sekundaer" disabled>Derzeit nicht verfügbar</button>`;
    const geld = (n) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    const gruppe = (name, liste) =>
      liste.length
        ? `<p class="x-quickadd__gruppe-titel">${name}</p><div class="x-quickadd__gruppe">${liste
            .map(
              (v) => `<label class="x-quickadd__option"><input type="radio" name="id" value="${v.id}" data-titel="${v.titel}" data-preis="${geld(v.preis)}"><span>${v.titel.replace(' Essence', '')}</span><span class="x-quickadd__preis">${geld(v.preis)}</span></label>`,
            )
            .join('')}</div>`
        : '';
    const flakon = vs.filter((v) => !v.titel.includes('Essence'));
    const essence = vs.filter((v) => v.titel.includes('Essence'));
    const qid = `quiz-${p.handle}`;
    return `<product-form class="x-quickadd" data-x-quickadd>
      <form method="post" action="/cart/add" id="x-qa-form-${qid}" class="x-quickadd__form" novalidate>
        <button type="button" class="x-button x-button--primaer x-quickadd__knopf" data-x-qa-oeffnen aria-expanded="false" aria-controls="x-qa-panel-${qid}">In den Warenkorb</button>
        <div class="x-quickadd__panel" id="x-qa-panel-${qid}" hidden>
          <p class="x-quickadd__panel-titel">Größe wählen</p>
          ${gruppe('Flakon – Extrait de Parfum, 35 % Duftölanteil', flakon)}
          ${gruppe('Essence – pures Duftöl', essence)}
          <button type="submit" name="add" class="x-button x-button--primaer x-quickadd__senden" disabled><span>Hinzufügen <span data-x-qa-auswahl></span></span></button>
          <div class="loading__spinner hidden" aria-hidden="true"></div>
          <div class="product-form__error-message-wrapper x-quickadd__fehler" role="alert" hidden><span class="product-form__error-message"></span></div>
        </div>
      </form>
    </product-form>`;
  }

  familienName(slug) {
        return (
          {
            'frisch-zitrisch': 'Frisch & Zitrisch',
            'aquatisch-klar': 'Aquatisch & Klar',
            'aromatisch-gruen': 'Aromatisch & Grün',
            blumig: 'Blumig',
            'fruchtig-suess': 'Fruchtig & Süß',
            'holzig-wuerzig': 'Holzig & Würzig',
            'amber-orientalisch': 'Amber & Orientalisch',
            gourmand: 'Gourmand',
          }[slug] ?? slug
        );
      }

      anlassName(a) {
        return (
          {
            alltag: 'Alltag',
            büro: 'Büro',
            abend: 'Abend',
            besonderer_anlass: 'besondere Anlässe',
            sport_freizeit: 'Sport & Freizeit',
          }[a] ?? a
        );
      }

      renderErgebnis() {
        const bewertet = this.produkte
          .map((p) => this.bewerte(p))
          .filter(Boolean)
          .sort((x, y) => y.score - x.score)
          .slice(0, 5);

        const teilen = btoa(JSON.stringify(this.antworten));
        const url = `${location.pathname}?q=${teilen}`;

        this.innerHTML = `
          <div class="x-quiz">
            <div class="x-quiz__fortschritt"><div class="x-quiz__fortschritt-balken" style="--quiz-fortschritt:1"></div></div>
            <h2 class="x-quiz__frage">Das passt zu deinen Antworten</h2>
            ${
              bewertet.length === 0
                ? `<p>Keine Treffer für diese Kombination — probiere weniger Einschränkungen.</p>`
                : `<div class="x-quiz__ergebnisse">${bewertet
                    .map(
                      ({ p, gruende }) => `
                    <article class="x-product-card">
                      <a class="x-product-card__link" href="/products/${p.handle}">
                        <div class="x-product-card__media">${
                          p.bild
                            ? `<img class="x-product-card__bild" src="${p.bild}?width=600" srcset="${p.bild}?width=400 400w, ${p.bild}?width=600 600w, ${p.bild}?width=800 800w" sizes="(min-width: 990px) 30vw, 90vw" width="820" height="1024" loading="lazy" alt="${p.code} ${p.name || ''} – Flakon">`
                            : `<div class="x-product-card__platzhalter"><span class="x-product-card__platzhalter-no">${p.code}</span></div>`
                        }</div>
                        <h3 class="x-product-card__name">${p.name || p.code}</h3>
                        <p class="x-product-card__meta">${this.familienName(p.familie)}</p>
                      </a>
                      <ul class="x-product-card__akkorde">${p.akkorde.map((x) => `<li>${x}</li>`).join('')}</ul>
                      <p class="x-quiz__begruendung">Weil du ${gruende.slice(0, 2).join(' + ') || 'breit gewählt'} gewählt hast: ${p.kurzprofil}</p>
                      ${this.quickAdd(p)}
                    </article>`,
                    )
                    .join('')}</div>`
            }
            <div class="x-quiz__weiter">
              <button type="button" class="x-button x-button--sekundaer" data-neustart>Neu starten</button>
              <button type="button" class="x-button x-button--sekundaer" data-teilen>Ergebnis-Link kopieren</button>
            </div>
          </div>`;

        this.querySelector('[data-neustart]').addEventListener('click', () => {
          this.antworten = {};
          this.schritt = 0;
          history.replaceState({}, '', location.pathname);
          this.render();
        });
        this.querySelector('[data-teilen]').addEventListener('click', async (e) => {
          history.replaceState({}, '', url);
          try {
            await navigator.clipboard.writeText(location.href);
            e.target.textContent = 'Kopiert!';
          } catch {
            e.target.textContent = location.href;
          }
        });
      }
    },
  );
}
